import {
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
  signInWithRedirect,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword,
  signIn as amplifySignIn,
  confirmSignIn as amplifyConfirmSignIn,
  updatePassword,
  updateUserAttributes as amplifyUpdateUserAttributes,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  updateMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
  fetchMFAPreference,
  deleteUser as amplifyDeleteUser
} from "aws-amplify/auth";

export const MFA_TYPES = Object.freeze({
  TOTP: "TOTP",
  SMS: "SMS",
  NONE: "NOMFA"
});

// Amplify v6 errors use `name` instead of `code`. Normalize so existing
// `error.code` checks throughout the codebase continue to work.
function normalizeError(error) {
  if (error?.name && !error.code) {
    error.code = error.name;
  }
  throw error;
}

const signUp = async oldParams => {
  const { username, password, attributes, validationData, clientMetadata } = oldParams;
  const newParams = {
    username,
    password,
    options: {
      userAttributes: attributes,
      validationData,
      clientMetadata
      // autoSignIn: true
    }
  };
  return amplifySignUp(newParams).catch(normalizeError);
};

const signIn = async (email, password, clientMetadata) => {
  const newParams = {
    username: email,
    password,
    ...(clientMetadata && { options: { clientMetadata } })
  };
  return amplifySignIn(newParams).catch(normalizeError);
};

const confirmSignUp = async (username, code, options) => {
  const newParams = {
    username,
    confirmationCode: code,
    ...(options && { options })
  };
  return amplifyConfirmSignUp(newParams).catch(normalizeError);
};

const currentAuthenticatedUser = async options => {
  // In v6, bypassCache is handled by fetchAuthSession with { forceRefresh: true }
  const forceRefresh = options?.bypassCache || false;

  const { username, signInDetails } = await getCurrentUser().catch(normalizeError);
  const { tokens: session } = await fetchAuthSession({ forceRefresh }).catch(normalizeError);

  if (!session) {
    throw new Error("No session found");
  }

  // Return structure that mimics v5 CognitoUser for backward compatibility
  return {
    username,
    signInUserSession: session,
    authenticationFlowType: signInDetails?.authFlowType
  };
};

const currentSession = async options => {
  const { tokens: session } = await fetchAuthSession(options).catch(normalizeError);
  if (!session) {
    throw new Error("No session found");
  }
  return session;
};

const signOut = async () => {
  return amplifySignOut().catch(normalizeError);
};

const federatedSignIn = async oldParams => {
  const newParams = oldParams;
  const result = await signInWithRedirect(newParams).catch(normalizeError);
  return result;
};

const resendSignUp = async email => {
  return resendSignUpCode({ username: email }).catch(normalizeError);
};

const forgotPassword = async (email, clientMetadata) => {
  return amplifyResetPassword({
    username: email,
    ...(clientMetadata && { options: { clientMetadata } })
  }).catch(normalizeError);
};

const forgotPasswordSubmit = async (email, code, newPassword, clientMetadata) => {
  return confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
    ...(clientMetadata && { options: { clientMetadata } })
  }).catch(normalizeError);
};

const confirmSignIn = async (user, challengeResponse, mfaType, clientMetadata) => {
  return amplifyConfirmSignIn({
    challengeResponse,
    ...(clientMetadata && { options: { clientMetadata } })
  }).catch(normalizeError);
};

const changePassword = async (user, oldPassword, newPassword) => {
  return updatePassword({
    oldPassword,
    newPassword
  }).catch(normalizeError);
};

const completeNewPassword = async (user, newPassword, requiredAttributes, clientMetadata) => {
  return amplifyConfirmSignIn({
    challengeResponse: newPassword,
    ...(requiredAttributes &&
      Object.keys(requiredAttributes).length > 0 && {
        options: {
          userAttributes: requiredAttributes,
          ...(clientMetadata && { clientMetadata })
        }
      }),
    ...(!requiredAttributes &&
      clientMetadata && {
        options: { clientMetadata }
      })
  }).catch(normalizeError);
};

const updateUserAttributes = async (user, attributes) => {
  return amplifyUpdateUserAttributes({ userAttributes: attributes }).catch(normalizeError);
};

const verifyCurrentUserAttribute = async attribute => {
  return sendUserAttributeVerificationCode({
    userAttributeKey: attribute
  }).catch(normalizeError);
};

const verifyCurrentUserAttributeSubmit = async (attribute, code) => {
  return confirmUserAttribute({
    userAttributeKey: attribute,
    confirmationCode: code
  }).catch(normalizeError);
};

const setPreferredMFA = async mfaMethod => {
  const mfaSettings = {};

  switch (mfaMethod) {
    case "TOTP":
    case MFA_TYPES.TOTP:
      mfaSettings.totp = "PREFERRED";
      mfaSettings.sms = "DISABLED";
      break;
    case "SMS":
    case MFA_TYPES.SMS:
      mfaSettings.sms = "PREFERRED";
      mfaSettings.totp = "DISABLED";
      break;
    case MFA_TYPES.NONE:
      mfaSettings.sms = "DISABLED";
      mfaSettings.totp = "DISABLED";
      break;
  }

  return updateMFAPreference(mfaSettings).catch(normalizeError);
};

const setupTOTP = async user => {
  const totpSetupDetails = await setUpTOTP().catch(normalizeError);
  return totpSetupDetails.sharedSecret;
};

const verifyTotpToken = async challengeAnswer => {
  await verifyTOTPSetup({
    code: challengeAnswer
  }).catch(normalizeError);
  return { Status: "SUCCESS" };
};

const getPreferredMFA = async () => {
  const isLoggedInUsingThirdParty = await isLoggedInUsingThirdPartyProvider();
  if (!isLoggedInUsingThirdParty) {
    const { preferred } = await fetchMFAPreference().catch(normalizeError);
    return preferred;
  }

  return undefined;
};

const deleteUser = async () => {
  return amplifyDeleteUser().catch(normalizeError);
};

// Utility functions
// @TODO: Remove this function after all the clients are updated to use getUserAttributes. This is only to handle v5 users already logged in case.
export const isLoggedInUsingThirdPartyProvider = async () => {
  const { tokens: session } = await fetchAuthSession().catch(normalizeError);

  // If idToken has "at_hash" then it is a third party login
  if (session?.idToken?.payload?.at_hash) {
    return true;
  }

  return false;
};

// @TODO: Keeping the original isLoggedInUsingThirdPartyProvider
// export const isLoggedInUsingThirdPartyProvider = async () => {
//   const { signInDetails } = await getCurrentUser();
//   if (signInDetails) {
//     return false;
//   } else {
//     return true;
//   }
// };

export const verifySigninPassword = async password => {
  await updatePassword({
    oldPassword: password,
    newPassword: password
  }).catch(normalizeError);
  return true; // Password is correct
};

export const getUserAttributes = async () => {
  const session = await fetchAuthSession().catch(normalizeError);
  const payload = session.tokens?.idToken?.payload;

  if (!payload) return null;

  // Extract standard attributes
  const { email, email_verified, phone_number, phone_number_verified, sub, ...customAttrs } = payload;

  let formattedPhoneNumber = formatPhoneNumber(phone_number);
  const attributes = {
    email,
    email_verified: String(email_verified),
    phone_number: formattedPhoneNumber,
    phone_number_verified: String(phone_number_verified),
    sub,
    ...customAttrs
  };
  return attributes;
};

export const formatPhoneNumber = phone => {
  if (!phone) return null;
  let formattedPhoneNumber = phone.replace(/\++/g, "+").replace(/[\s-()]/g, "");
  formattedPhoneNumber = /^\d/.test(formattedPhoneNumber) ? `+${formattedPhoneNumber}` : formattedPhoneNumber;
  return formattedPhoneNumber;
};

// Default export that mimics the old Auth object structure
const Auth = {
  signUp,
  signIn,
  confirmSignUp,
  currentAuthenticatedUser,
  currentSession,
  signOut,
  federatedSignIn,
  resendSignUp,
  forgotPassword,
  forgotPasswordSubmit,
  confirmSignIn,
  changePassword,
  completeNewPassword,
  updateUserAttributes,
  verifyCurrentUserAttribute,
  verifyCurrentUserAttributeSubmit,
  setPreferredMFA,
  setupTOTP,
  verifyTotpToken,
  getPreferredMFA,
  deleteUser
};

export default Auth;
